import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./index.module.css";
import { useAppDispatch } from "../../../slice/hooks";
import { setActiveCategory } from "../../../slice/category-slice";

function CategoryList({
  categories,
  handleCategoryChange = () => {},
  handleSubCategoryChange = () => {},
  activeCategory,
  activeSubCategory,
  useFloatingDropdown = false,
}) {
  const categoryRef = useRef(null);
  const itemRefs = useRef({});
  const closeTimerRef = useRef(null);
  const openTimerRef = useRef(null);
  const pendingDropdownCategoryRef = useRef(null);
  const dispatch = useAppDispatch();
  const [openDropdownCategory, setOpenDropdownCategory] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const clearCloseTimer = () => {
    if (!closeTimerRef.current) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const closeDropdown = () => {
    clearCloseTimer();
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    pendingDropdownCategoryRef.current = null;
    setOpenDropdownCategory(null);
  };

  const scheduleCloseDropdown = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpenDropdownCategory(null);
      closeTimerRef.current = null;
    }, 120);
  };

  const updateDropdownPosition = (categoryId) => {
    if (!useFloatingDropdown || typeof window === "undefined") return;

    const item = itemRefs.current[categoryId];
    if (!item) return;

    const rect = item.getBoundingClientRect();
    const dropdownWidth = Math.min(280, window.innerWidth - 32);
    const left = Math.min(Math.max(rect.left, 16), window.innerWidth - dropdownWidth - 16);

    setDropdownPosition({
      top: rect.bottom,
      left,
      width: Math.max(rect.width, 200),
    });
  };

  const openDropdown = (categoryId) => {
    clearCloseTimer();
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    pendingDropdownCategoryRef.current = null;
    updateDropdownPosition(categoryId);
    setOpenDropdownCategory(categoryId);
  };

  const scrollCategoryIntoView = (categoryId, behavior = "smooth") => {
    const item = itemRefs.current[categoryId];
    if (!item) return false;

    item.scrollIntoView({
      behavior,
      inline: "center",
      block: "nearest",
    });

    return true;
  };

  const openPendingDropdown = () => {
    const categoryId = pendingDropdownCategoryRef.current;
    if (!categoryId) return;

    updateDropdownPosition(categoryId);
    setOpenDropdownCategory(categoryId);
    pendingDropdownCategoryRef.current = null;
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const schedulePendingDropdownOpen = (delay = 180) => {
    if (!pendingDropdownCategoryRef.current || typeof window === "undefined") return;

    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
    }

    openTimerRef.current = window.setTimeout(openPendingDropdown, delay);
  };

  const openDropdownAfterScroll = (categoryId) => {
    clearCloseTimer();
    setOpenDropdownCategory(null);
    pendingDropdownCategoryRef.current = categoryId;
    scrollCategoryIntoView(categoryId);
    schedulePendingDropdownOpen(240);
  };

  const containsRelatedTarget = (currentTarget, relatedTarget) => {
    return (
      typeof Node !== "undefined" &&
      relatedTarget instanceof Node &&
      currentTarget.contains(relatedTarget)
    );
  };

  useEffect(() => {
    if (activeCategory && pendingDropdownCategoryRef.current !== activeCategory) {
      scrollCategoryIntoView(activeCategory);
    }
  }, [activeCategory]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
      }
    };
  }, []);

  return (
	    <div
	      ref={categoryRef}
	      className={styles.categoryList}
	      onScroll={() => {
	        if (pendingDropdownCategoryRef.current) {
	          schedulePendingDropdownOpen();
	          return;
	        }
	        closeDropdown();
	      }}
	    >
      {categories &&
        categories.map((i) => {
          const subCategories = Array.isArray(i.subCategories) ? i.subCategories : [];
          const hasSubCategories = subCategories.length > 0;
          const isActiveCategory = activeCategory === i.productCategoryId;

          return (
            <div
              key={i.productCategoryId}
              className={styles.categoryItemWrapper}
              ref={(el) => (itemRefs.current[i.productCategoryId] = el)}
              onMouseEnter={() => {
                if (!hasSubCategories) return;
                openDropdown(i.productCategoryId);
              }}
              onMouseLeave={(e) => {
                if (containsRelatedTarget(e.currentTarget, e.relatedTarget)) return;
                if (useFloatingDropdown) {
                  scheduleCloseDropdown();
                  return;
                }
                closeDropdown();
              }}
              onFocus={() => {
                if (!hasSubCategories) return;
                openDropdown(i.productCategoryId);
              }}
              onBlur={(e) => {
                if (containsRelatedTarget(e.currentTarget, e.relatedTarget)) return;
                if (useFloatingDropdown) {
                  scheduleCloseDropdown();
                  return;
                }
                closeDropdown();
              }}
            >
              <button
                type="button"
                className={`${styles.categoryItem} ${
                  isActiveCategory ? styles.active : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  const newActiveCategory =
                    isActiveCategory && !activeSubCategory
                      ? null
                      : i.productCategoryId;

                  dispatch(setActiveCategory(newActiveCategory));
                  handleCategoryChange(newActiveCategory);
                  if (hasSubCategories && newActiveCategory) {
                    openDropdownAfterScroll(i.productCategoryId);
                  } else {
                    closeDropdown();
                  }
                }}
                aria-haspopup={hasSubCategories ? "menu" : undefined}
                aria-expanded={
                  hasSubCategories
                    ? openDropdownCategory === i.productCategoryId
                    : undefined
                }
              >
                <span>{i.name}</span>
                {hasSubCategories && <span className={styles.categoryChevron}>▾</span>}
              </button>

              {hasSubCategories &&
                (() => {
                  const dropdown = (
                    <div
                      className={`${styles.subCategoryDropdown} ${
                        openDropdownCategory === i.productCategoryId
                          ? styles.openSubCategoryDropdown
                          : ""
                      } ${
                        useFloatingDropdown ? styles.floatingSubCategoryDropdown : ""
                      }`}
                      style={
                        useFloatingDropdown && dropdownPosition
                          ? {
                              top: dropdownPosition.top,
                              left: dropdownPosition.left,
                              minWidth: dropdownPosition.width,
                            }
                          : null
                      }
                      role="menu"
                      onMouseEnter={clearCloseTimer}
                      onMouseLeave={
                        useFloatingDropdown ? scheduleCloseDropdown : undefined
                      }
                    >
                      {subCategories.map((subCategory) => (
                        <button
                          key={subCategory.subCategoryId}
                          type="button"
                          role="menuitem"
                          className={`${styles.subCategoryItem} ${
                            activeSubCategory === subCategory.subCategoryId
                              ? styles.activeSubCategory
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(setActiveCategory(i.productCategoryId));
                            closeDropdown();
                            e.currentTarget.blur();
                            handleSubCategoryChange({
                              categoryId: i.productCategoryId,
                              subCategoryId: subCategory.subCategoryId,
                              name: subCategory.name,
                            });
                          }}
                        >
                          {subCategory.name}
                        </button>
                      ))}
                    </div>
                  );

                  if (
                    useFloatingDropdown &&
                    typeof document !== "undefined" &&
                    openDropdownCategory === i.productCategoryId
                  ) {
                    return createPortal(dropdown, document.body);
                  }

                  return dropdown;
                })()}
            </div>
          );
        })}
    </div>
  );
}

export default CategoryList;
